import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, Users, CreditCard, LogOut, ShieldCheck, X, FlaskConical } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { adminService } from '@/services/admin.service';
import { useEffect } from 'react';
import { Avatar } from './Avatar';

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Admin Management', href: '/admin-management', icon: Users },
  { name: 'Access Control', href: '/access-control', icon: ShieldCheck },
  { name: 'Subscriptions', href: '/subscriptions', icon: CreditCard },
  { name: 'Testing – Users', href: '/testing/users', icon: FlaskConical },
];

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const location = useLocation();
  const { logout, user } = useAuth();
  const navigate = useNavigate();

  // Fetch latest profile to get updated profile image
  const { data: profile } = useQuery({
    queryKey: ['myProfile'],
    queryFn: async () => {
      const data = await adminService.getMyProfile();
      return data;
    },
    enabled: !!user, // Only fetch if user is logged in
    refetchOnWindowFocus: false,
  });

  // Prioritize profile query result over user from AuthContext
  // This ensures we show the most up-to-date image
  const profileImage = profile?.profileImage;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Close sidebar when route changes on mobile
  useEffect(() => {
    if (isOpen) {
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
          fixed lg:static inset-y-0 left-0 z-50
          w-64 flex flex-col
          bg-foreground text-background border-r border-border
          transform transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
          <div className="flex items-center justify-between flex-shrink-0 px-4 mb-4">
            <h1 className="text-xl font-bold text-background">Our Digi Folio Admin</h1>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="lg:hidden text-background hover:bg-background/10"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
          <nav className="mt-5 flex-1 px-2 space-y-1">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
              
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`
                    group flex items-center px-2 py-2 text-sm font-medium rounded-md
                    ${
                      isActive
                        ? 'bg-primary text-primary-foreground'
                        : 'text-background/70 hover:bg-background/10 hover:text-background'
                    }
                  `}
                >
                  <item.icon
                    className={`mr-3 flex-shrink-0 h-5 w-5 ${
                      isActive ? 'text-primary-foreground' : 'text-background/70 group-hover:text-background'
                    }`}
                  />
                  {item.name}
                </Link>
              );
            })}
          </nav>
          <div className="flex-shrink-0 flex flex-col border-t border-background/20">
            <div className="p-2">
              <Link
                to="/my-profile"
                className={`
                  group flex items-center px-2 py-2 text-sm font-medium rounded-md
                  ${
                    location.pathname === '/my-profile' || location.pathname.startsWith('/my-profile/')
                      ? 'bg-primary text-primary-foreground'
                      : 'text-background/70 hover:bg-background/10 hover:text-background'
                  }
                `}
              >
                <Avatar
                  imageUrl={profileImage}
                  name={profile?.name || user?.name}
                  size="sm"
                  variant="dark"
                  className="mr-3"
                  alt={user?.name || 'Profile'}
                />
                My Profile
              </Link>
            </div>
            <div className="px-2 pb-2">
              <Button
                variant="ghost"
                onClick={handleLogout}
                className="w-full justify-start text-background/70 hover:text-background hover:bg-background/10"
              >
                <LogOut className="mr-3 h-5 w-5" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

